import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { listBranchTablesWithQrs, getPublicMenuByQrToken } from "../../services/supabase/qrService";
import { startStaffTabletQrSession } from "../../services/supabase/qrCustomerSessionService";
import { getBranchTablesSnapshot } from "../../services/local/branchTablesSnapshot";
import { createTabletOrderSession, clearTabletOrderSession, getLauncherTables } from "../../services/local/tabletOrderLauncher";
import MenuComensalQR from "../paginas/MenuComensalQr";
import "../../styles/TabletOrderLauncher.css";

export default function TabletOrderLauncher({ branchId, branchName, localMode = false }) {
  const [open, setOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [message, setMessage] = useState("");
  const [session, setSession] = useState(null);
  const [openingTable, setOpeningTable] = useState("");
  const [progress, setProgress] = useState({ hasDraft: false, busy: false, submitted: false });
  const [confirmReturn, setConfirmReturn] = useState(false);
  const [pin, setPin] = useState("");
  const [exitPin, setExitPin] = useState("");
  const [exitPrompt, setExitPrompt] = useState(false);
  const rootRef = useRef(null);
  const sessionRef = useRef(null);
  const generation = useRef(0);
  sessionRef.current = session;

  useEffect(() => () => {
    generation.current += 1;
    clearTabletOrderSession(sessionRef.current?.key);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const background = Array.from(document.body.children).filter((element) => element !== rootRef.current);
    const inertStates = background.map((element) => element.inert);
    background.forEach((element) => { element.inert = true; });
    document.body.style.overflow = "hidden";
    rootRef.current?.querySelector("button:not([disabled])")?.focus();
    const warnBeforeExit = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warnBeforeExit);
    return () => {
      document.body.style.overflow = previous;
      background.forEach((element, index) => { element.inert = inertStates[index]; });
      previousFocus?.focus?.();
      window.removeEventListener("beforeunload", warnBeforeExit);
    };
  }, [open]);

  useEffect(() => {
    if (!open || session) return undefined;
    let active = true;
    setLoading(true);
    setMessage("");
    setTables([]);
    const request = localMode
      ? Promise.resolve(getBranchTablesSnapshot(branchId))
      : listBranchTablesWithQrs(branchId);
    request.then((rows) => { if (active) setTables(getLauncherTables(rows)); })
      .catch((error) => { if (active) setMessage(error.message || "No se pudieron cargar las mesas."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, session, branchId, revision, localMode]);

  const chooseTable = async (table) => {
    if (openingTable) return;
    const requestGeneration = ++generation.current;
    setOpeningTable(table.id);
    setMessage("");
    try {
      if (localMode) throw new Error("Para enviar pedidos desde la tablet, entra con una cuenta de la sucursal conectada. El modo creador local no publica pedidos.");
      await startStaffTabletQrSession(table.qr.token);
      const menu = await getPublicMenuByQrToken(table.qr.token, { forceRefresh: true });
      if (menu?.branch?.id !== branchId || menu?.table?.id !== table.id) throw new Error("El QR no corresponde a esta mesa y sucursal. Actualiza las mesas.");
      if (generation.current !== requestGeneration) return;
      setProgress({ hasDraft: false, busy: false, submitted: false });
      setSession(createTabletOrderSession(table));
    } catch (error) {
      if (generation.current === requestGeneration) setMessage(error.message || "No se pudo abrir la carta de esta mesa.");
    } finally {
      if (generation.current === requestGeneration) setOpeningTable("");
    }
  };

  const returnToTables = () => {
    if (progress.busy) return;
    clearTabletOrderSession(session?.key);
    setSession(null);
    setConfirmReturn(false);
    setMessage("");
  };

  const closeLauncher = () => {
    if (exitPin !== pin) { setMessage("El PIN de salida no coincide."); return; }
    generation.current += 1;
    setOpen(false);
    setOpeningTable("");
    setExitPrompt(false);
    setExitPin("");
    setPin("");
    setMessage("");
    if (document.fullscreenElement === rootRef.current) document.exitFullscreen?.().catch(() => {});
  };

  return <>
    <section className="tablet-launch-entry">
      <div><span className="tablet-launch-eyebrow">PEDIDOS EN TABLET</span><h2>Lanzador de mesas</h2>
        <p>Una sola cuadricula de mesas activas. Para que el cliente pida o para una tablet compartida por los mozos.</p>
        <small>Los pedidos se envian para pagar despues. No libera mesas ni abre billeteras en la tablet.</small>
      </div>
      <div className="tablet-launch-start">
        <label>PIN para salir del lanzador<input aria-label="PIN para salir del lanzador" type="password" inputMode="numeric" autoComplete="off" maxLength={8} value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))} placeholder="4 a 8 numeros" /></label>
        <button type="button" disabled={!branchId || pin.length < 4} onClick={() => { setOpen(true); setMessage(""); }}>Abrir lanzador</button>
        <small>El PIN protege la salida dentro de la app. Para entregarla al cliente, activa tambien el bloqueo de pantalla o modo kiosco del dispositivo.</small>
      </div>
    </section>
    {open && createPortal(<div className="tablet-launcher" ref={rootRef} role="dialog" aria-modal="true" aria-label="Lanzador de mesas">
      <header className="tablet-launcher-header">
        <div><span className="tablet-launch-eyebrow">{branchName || "Sucursal"}</span><h1>{session ? session.table.label || `Mesa ${session.table.tableNumber}` : "Elegi una mesa"}</h1></div>
        <div className="tablet-launcher-actions">
          {session ? <button disabled={progress.busy} onClick={() => progress.hasDraft ? setConfirmReturn(true) : returnToTables()}>{progress.submitted ? "Otra mesa / nuevo pedido" : "Volver a mesas"}</button> : <>
            <button disabled={loading || Boolean(openingTable)} onClick={() => setRevision((value) => value + 1)}>Actualizar mesas</button>
            <button onClick={() => rootRef.current?.requestFullscreen?.().catch(() => setMessage("Puedes usar el lanzador sin pantalla completa."))}>Pantalla completa</button>
            <button disabled={Boolean(openingTable)} onClick={() => { setExitPrompt(true); setMessage(""); }}>Salir</button>
          </>}
        </div>
      </header>
      {session ? <div className="tablet-launcher-menu"><MenuComensalQR key={session.key} qrTokenOverride={session.table.qr.token} tabletSessionKey={session.key} onTabletOrderStateChange={setProgress} /></div> :
        <main className="tablet-launcher-content">
          <p>Selecciona la mesa para abrir su carta. Cada nuevo uso comienza sin datos del comensal anterior.</p>
          <p className="tablet-launcher-note">Se respetan los accesos QR habilitados. Si un QR esta cerrado, habilitalo desde Qr por mesa; este lanzador no cambia sus permisos.</p>
          {message && <p role="alert" className="tablet-launcher-notice">{message}</p>}
          {loading ? <p role="status">Cargando mesas...</p> : <div className="tablet-launcher-grid">{tables.map((table) => <button className="tablet-launcher-table" key={table.id} disabled={!table.qr?.token || Boolean(openingTable)} onClick={() => chooseTable(table)}>
            <span>MESA</span><strong>{table.tableNumber}</strong><span>{table.label || `Mesa ${table.tableNumber}`}</span><small>{openingTable === table.id ? "Abriendo carta..." : !table.qr?.token ? "Sin QR configurado" : "Abrir carta"}</small>
          </button>)}</div>}
          {!loading && !message && !tables.length && <p>No hay mesas activas en esta sucursal.</p>}
        </main>}
      {confirmReturn && <div className="tablet-launcher-overlay"><section role="alertdialog" aria-label="Pedido sin enviar"><h2>Hay productos sin enviar</h2><p>Si volves a las mesas, se descartara este borrador. Los pedidos ya enviados no se modifican.</p><button onClick={() => setConfirmReturn(false)}>Seguir con el pedido</button><button onClick={returnToTables}>Descartar borrador y volver</button></section></div>}
      {exitPrompt && <div className="tablet-launcher-overlay"><form onSubmit={(event) => { event.preventDefault(); closeLauncher(); }}><h2>Salir del lanzador</h2><label>PIN de salida<input aria-label="PIN de salida" type="password" inputMode="numeric" autoFocus value={exitPin} onChange={(event) => setExitPin(event.target.value)} /></label>{message && <p role="alert">{message}</p>}<button type="button" onClick={() => { setExitPrompt(false); setExitPin(""); setMessage(""); }}>Seguir en mesas</button><button type="submit">Confirmar salida</button></form></div>}
    </div>, document.body)}
  </>;
}
